import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Navbar } from "../components/Navbar";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  Search, MapPin, User, X, MessageSquare, Briefcase, 
  Car, Languages, FileCheck, Loader2, Send, Clock, AlertCircle, Eye,
  Building2, Lock
} from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Safe import with fallback
let SETTORI_SKILLS = {};
let PERMESSI_LAVORO = [];
try {
  const data = require("../data/settoriSkills");
  SETTORI_SKILLS = data.SETTORI_SKILLS || {};
  PERMESSI_LAVORO = data.PERMESSI_LAVORO || [];
} catch (e) {
  console.error("Failed to load settoriSkills:", e);
}

const TICINO_LOCATIONS = [
  { value: "all", label: "Tutte le località" },
  { value: "Residente in Italia (Frontaliere)", label: "Frontaliere (Italia)" },
  { value: "Lugano", label: "Lugano" },
  { value: "Bellinzona", label: "Bellinzona" },
  { value: "Locarno", label: "Locarno" },
  { value: "Mendrisio", label: "Mendrisio" },
  { value: "Chiasso", label: "Chiasso" },
  { value: "Ascona", label: "Ascona" },
  { value: "Biasca", label: "Biasca" }
];

const DISPONIBILITA_OPTIONS = [
  { value: "all", label: "Tutte le disponibilità" },
  { value: "subito", label: "Subito" },
  { value: "2giorni", label: "2 giorni" },
  { value: "1settimana", label: "Una settimana" },
  { value: "2settimane", label: "Due settimane" },
  { value: "1mese", label: "Un mese" },
  { value: "2mesi", label: "Due mesi" },
  { value: "3mesi", label: "3 mesi" }
];

// Safe getter for settori keys
const getSettoriKeys = () => {
  try {
    return Object.keys(SETTORI_SKILLS || {});
  } catch (e) {
    console.error("Error getting settori keys:", e);
    return [];
  }
};

export default function CandidatesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Check if user is authorized (company or agency)
  const isAuthorized = user && (user.user_type === "company" || user.user_type === "agency");
  
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [filters, setFilters] = useState({
    search: searchParams.get("search") || "",
    location: searchParams.get("location") || "all",
    settore: searchParams.get("settore") || "all",
    disponibilita: searchParams.get("disponibilita") || "all"
  });

  // Contact modal state
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [contactForm, setContactForm] = useState({ subject: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (isAuthorized) {
      fetchCandidates();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isAuthorized]);

  const fetchCandidates = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      const searchVal = searchParams.get("search");
      const locationVal = searchParams.get("location");
      
      if (searchVal) params.append("search", searchVal);
      if (locationVal && locationVal !== "all") {
        params.append("location", locationVal);
      }
      
      const response = await axios.get(`${API}/candidates?${params.toString()}`);
      let data = Array.isArray(response.data) ? response.data : [];
      
      // Filter by settore client-side
      const settore = searchParams.get("settore");
      if (settore && settore !== "all" && data.length > 0) {
        data = data.filter(c => c && c.settore === settore);
      }
      
      // Filter by disponibilita client-side
      const disponibilita = searchParams.get("disponibilita");
      if (disponibilita && disponibilita !== "all" && data.length > 0) {
        data = data.filter(c => c && c.disponibilita === disponibilita);
      }
      
      setCandidates(data);
    } catch (err) {
      console.error("Failed to fetch candidates:", err);
      setError("Errore nel caricamento dei candidati");
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    // Ensure value is never undefined
    const safeValue = value || "all";
    setFilters(prev => ({ ...prev, [key]: safeValue }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const newParams = new URLSearchParams();
    if (filters.search) newParams.set("search", filters.search);
    if (filters.location && filters.location !== "all") newParams.set("location", filters.location);
    if (filters.settore && filters.settore !== "all") newParams.set("settore", filters.settore);
    if (filters.disponibilita && filters.disponibilita !== "all") newParams.set("disponibilita", filters.disponibilita);
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setFilters({ search: "", location: "all", settore: "all", disponibilita: "all" });
    setSearchParams(new URLSearchParams());
  };

  const hasActiveFilters = filters.search || 
    (filters.location && filters.location !== "all") || 
    (filters.settore && filters.settore !== "all") ||
    (filters.disponibilita && filters.disponibilita !== "all");

  const handleContactClick = (candidate) => {
    if (!candidate) return;
    
    if (!user) {
      navigate("/login");
      return;
    }
    
    if (user.user_type !== "company") {
      toast.error("Solo le aziende possono contattare i candidati");
      return;
    }

    setSelectedCandidate(candidate);
    setContactForm({ 
      subject: `Opportunità lavorativa - ${user.company_name || user.name || "Azienda"}`,
      message: "" 
    });
    setShowContactModal(true);
  };

  const handleSendMessage = async () => {
    if (!contactForm.subject?.trim() || !contactForm.message?.trim()) {
      toast.error("Compila tutti i campi");
      return;
    }

    if (!selectedCandidate?.id) {
      toast.error("Candidato non valido");
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/messages`, {
        recipient_id: selectedCandidate.id,
        subject: contactForm.subject,
        content: contactForm.message
      });
      toast.success("Messaggio inviato al candidato!");
      setShowContactModal(false);
      setSelectedCandidate(null);
      setContactForm({ subject: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'invio del messaggio");
    } finally {
      setSending(false);
    }
  };

  const parseLanguages = (lingueStr) => {
    if (!lingueStr) return [];
    try {
      const parsed = JSON.parse(lingueStr);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const getPermessoLabel = (value) => {
    if (!value) return "";
    const permesso = (PERMESSI_LAVORO || []).find(p => p && p.value === value);
    return permesso ? permesso.label : value;
  };

  const getDisponibilitaLabel = (value) => {
    const labels = {
      "subito": "Subito",
      "2giorni": "2 giorni",
      "1settimana": "Una settimana",
      "2settimane": "Due settimane",
      "1mese": "Un mese",
      "2mesi": "Due mesi",
      "3mesi": "3 mesi"
    };
    return labels[value] || value || "";
  };

  // Safe render of skills
  const renderSkills = (skillsStr) => {
    if (!skillsStr || typeof skillsStr !== "string") return null;
    const skillsArray = skillsStr.split(" | ").filter(Boolean);
    if (skillsArray.length === 0) return null;
    
    return (
      <div className="flex flex-wrap gap-1 mt-2">
        {skillsArray.slice(0, 4).map((skill) => (
          <span key={`skill-${skill}`} className="text-xs px-2 py-0.5 bg-slate-100 rounded-full text-slate-600">
            {skill}
          </span>
        ))}
        {skillsArray.length > 4 && (
          <span className="text-xs px-2 py-0.5 bg-slate-100 rounded-full text-slate-500">
            +{skillsArray.length - 4}
          </span>
        )}
      </div>
    );
  };

  const settoriKeys = getSettoriKeys();

  // Access Denied Screen for non-authorized users
  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]" data-testid="candidates-page">
        <Navbar />
        
        <div className="bg-[#004E64] text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <h1 className="text-3xl font-bold mb-2">Zona Candidati</h1>
            <p className="text-blue-100">Trova talenti e professionisti in Canton Ticino</p>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Card className="text-center p-8">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Lock className="w-10 h-10 text-slate-400" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">Accesso Riservato</h2>
            <p className="text-slate-600 mb-6">
              La visualizzazione dei profili dei candidati è riservata alle <strong>Aziende</strong> e alle <strong>Agenzie di lavoro</strong> registrate.
            </p>
            
            {!user ? (
              <div className="space-y-4">
                <div className="inline-block bg-emerald-100 text-emerald-700 font-bold text-sm px-4 py-1.5 rounded-full mb-2">
                  REGISTRAZIONE GRATUITA
                </div>
                <p className="text-sm text-slate-500">Sei un'azienda o un'agenzia?</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link to="/register">
                    <Button className="w-full sm:w-auto bg-[#004E64] hover:bg-[#003847]">
                      <Building2 className="w-4 h-4 mr-2" />
                      Registra la tua azienda
                    </Button>
                  </Link>
                  <Link to="/login">
                    <Button variant="outline" className="w-full sm:w-auto">
                      Accedi
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-slate-500">
                  Il tuo account ({user.user_type === "candidate" ? "Candidato" : user.user_type === "service_provider" ? "Fornitore di Servizi" : user.user_type}) non ha accesso a questa sezione.
                </p>
                <p className="text-xs text-slate-400">
                  Se rappresenti un'azienda, contatta il supporto per aggiornare il tuo profilo.
                </p>
              </div>
            )}
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="candidates-page">
      <Navbar />
      
      <div className="bg-[#004E64] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl font-bold mb-2">Zona Candidati</h1>
          <p className="text-blue-100">Trova talenti e professionisti in Canton Ticino</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg border border-slate-200 p-4 mb-6">
          <form onSubmit={handleSearch}>
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input 
                  type="text" 
                  placeholder="Cerca per nome, competenze..." 
                  value={filters.search || ""} 
                  onChange={(e) => handleFilterChange("search", e.target.value)} 
                  className="pl-10" 
                />
              </div>
              
              {/* Location Filter */}
              <Select 
                value={filters.location || "all"} 
                onValueChange={(value) => handleFilterChange("location", value)}
              >
                <SelectTrigger className="w-full lg:w-[180px]">
                  <MapPin className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Località" />
                </SelectTrigger>
                <SelectContent>
                  {TICINO_LOCATIONS.map((loc) => (
                    <SelectItem key={loc.value} value={loc.value}>{loc.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Settore Filter */}
              <Select 
                value={filters.settore || "all"} 
                onValueChange={(value) => handleFilterChange("settore", value)}
              >
                <SelectTrigger className="w-full lg:w-[200px]">
                  <Briefcase className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Settore" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i settori</SelectItem>
                  {settoriKeys.map((settore) => (
                    <SelectItem key={settore} value={settore}>{settore}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Disponibilita Filter */}
              <Select 
                value={filters.disponibilita || "all"} 
                onValueChange={(value) => handleFilterChange("disponibilita", value)}
              >
                <SelectTrigger className="w-full lg:w-[200px]">
                  <Clock className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Disponibilità" />
                </SelectTrigger>
                <SelectContent>
                  {DISPONIBILITA_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button type="submit" className="bg-[#004E64] hover:bg-[#003B4D] text-white">
                Cerca
              </Button>
            </div>
            
            {hasActiveFilters && (
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-200">
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-[#DA291C]">
                  <X className="w-4 h-4 mr-1" /> Rimuovi filtri
                </Button>
              </div>
            )}
          </form>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Results count */}
        <div className="mb-4">
          <p className="text-slate-600">
            {loading ? "Caricamento..." : `${candidates.length} candidati trovati`}
          </p>
        </div>

        {/* Candidates Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : candidates.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {candidates.map((candidate) => {
              if (!candidate || !candidate.id) return null;
              
              const lingue = parseLanguages(candidate.lingue);
              
              return (
                <Card key={candidate.id} className="hover:shadow-lg transition-shadow overflow-hidden">
                  <CardContent className="p-0">
                    {/* Header */}
                    <div className="p-4 bg-gradient-to-r from-[#004E64] to-[#006B8A]">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                          <User className="w-6 h-6 text-white" />
                        </div>
                        <div className="text-white">
                          <h3 className="font-bold">{candidate.name || "Candidato"}</h3>
                          <div className="flex items-center gap-1 text-sm text-white/80">
                            <MapPin className="w-3 h-3" />
                            <span>{candidate.comune_residenza || "Ticino"}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-4 space-y-3">
                      {/* Settore e Skills */}
                      {candidate.settore && (
                        <div>
                          <Badge className="bg-[#DA291C]/10 text-[#DA291C] mb-2">
                            {candidate.settore}
                          </Badge>
                          {renderSkills(candidate.skills)}
                        </div>
                      )}

                      {/* Info Grid */}
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {candidate.permesso_lavoro && (
                          <div className="flex items-center gap-1.5 text-slate-600">
                            <FileCheck className="w-4 h-4 text-slate-400" />
                            <span className="truncate">{getPermessoLabel(candidate.permesso_lavoro)}</span>
                          </div>
                        )}
                        {candidate.has_auto && (
                          <div className="flex items-center gap-1.5 text-slate-600">
                            <Car className="w-4 h-4 text-slate-400" />
                            <span>Automunito</span>
                          </div>
                        )}
                        {candidate.anni_esperienza && (
                          <div className="flex items-center gap-1.5 text-slate-600">
                            <Briefcase className="w-4 h-4 text-slate-400" />
                            <span>{candidate.anni_esperienza} anni</span>
                          </div>
                        )}
                        {candidate.disponibilita && (
                          <div className="flex items-center gap-1.5 text-slate-600">
                            <Clock className="w-4 h-4 text-slate-400" />
                            <span>{getDisponibilitaLabel(candidate.disponibilita)}</span>
                          </div>
                        )}
                      </div>

                      {/* Lingue */}
                      {lingue.length > 0 && (
                        <div className="flex items-center gap-2">
                          <Languages className="w-4 h-4 text-slate-400" />
                          <div className="flex flex-wrap gap-1">
                            {lingue.slice(0, 3).map((l, i) => (
                              <span key={`lang-${l?.code || i}`} className="text-xs text-slate-600">
                                {l?.code?.toUpperCase() || "?"}
                                {i < Math.min(lingue.length, 3) - 1 && ","}
                              </span>
                            ))}
                            {lingue.length > 3 && (
                              <span className="text-xs text-slate-400">+{lingue.length - 3}</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="p-4 pt-0 flex gap-2">
                      <Button 
                        variant="outline"
                        onClick={() => navigate(`/candidates/${candidate.id}`)}
                        className="flex-1 border-[#004E64] text-[#004E64] hover:bg-[#004E64]/10"
                        data-testid={`view-candidate-${candidate.id}`}
                      >
                        <Eye className="w-4 h-4 mr-2" /> 
                        Vedi profilo
                      </Button>
                      <Button 
                        onClick={() => handleContactClick(candidate)}
                        className="flex-1 bg-[#004E64] hover:bg-[#003B4D] text-white"
                        data-testid={`contact-candidate-${candidate.id}`}
                      >
                        <MessageSquare className="w-4 h-4 mr-2" /> 
                        Contatta
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-lg border border-slate-200">
            <User className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Nessun candidato trovato</h3>
            <p className="text-slate-500 mb-4">
              {hasActiveFilters 
                ? "Nessun candidato corrisponde ai filtri selezionati" 
                : "I candidati con abbonamento attivo appariranno qui"}
            </p>
            {hasActiveFilters && (
              <Button variant="outline" onClick={clearFilters}>Rimuovi filtri</Button>
            )}
          </div>
        )}
      </div>

      {/* Contact Modal */}
      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Contatta {selectedCandidate?.name || "Candidato"}</DialogTitle>
            <DialogDescription>
              Invia un messaggio tramite il portale
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedCandidate && (
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-10 h-10 bg-[#004E64] rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-medium">{selectedCandidate.name || "Candidato"}</p>
                  <p className="text-sm text-slate-500">{selectedCandidate.comune_residenza || "Ticino"}</p>
                </div>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                Oggetto
              </label>
              <Input
                value={contactForm.subject || ""}
                onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                placeholder="es. Opportunità lavorativa"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                Messaggio
              </label>
              <Textarea
                value={contactForm.message || ""}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                placeholder="Scrivi il tuo messaggio al candidato..."
                rows={5}
              />
            </div>

            <p className="text-xs text-slate-500">
              Il candidato riceverà il messaggio nella sua casella del portale.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContactModal(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleSendMessage}
              disabled={sending}
              className="bg-[#DA291C] hover:bg-[#B91C1C]"
            >
              {sending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Invio...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Invia messaggio</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
